export class ClipboardError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ClipboardError'
  }
}

const fallbackCopy = (text: string) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  // Avoid scrolling
  textarea.style.top = '0'
  textarea.style.left = '0'
  textarea.style.position = 'fixed'
  // Avoid style flash
  textarea.style.padding = '0'
  textarea.style.background = 'transparent'
  textarea.style.border = 'none'
  textarea.style.outline = 'none'
  textarea.style.boxShadow = 'none'
  textarea.style.width = '2em'
  textarea.style.height = '2em'

  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()

  try {
    if (!document.execCommand('copy')) {
      throw 'Clipboard fallback failed'
    }
  } catch (err: any) {
    throw new ClipboardError(err?.message || 'Unable to copy to clipboard')
  } finally {
    if (textarea) {
      document.body.removeChild(textarea)
    }
  }
}

/**
 * Helper for copying text to the clipboard.
 *
 * It will try to use the modern Clipboard API if available (only in
 * 2021+ browser versions & in secure/HTTPS context), and if not, it
 * will fallback to `execCommand`, which is more widely supported but
 * deprecated.
 *
 * @param text Plain text to copy to the clipboard.
 * @throws {ClipboardError}
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  if (!navigator.clipboard) {
    fallbackCopy(text)
  }

  try {
    await navigator.clipboard.writeText(text)
  } catch (err) {
    throw new ClipboardError(
      err instanceof Error ? err.message : 'Clipboard API is unavailable'
    )
  }
}
